<template>
  <div class="checkbox-cashiers-tab">
    <div class="tab-toolbar">
      <b-button size="sm" variant="primary" @click="openAddModal">
        <i class="fa fa-plus"></i>
        Додати касира
      </b-button>
      <b-button size="sm" variant="outline-secondary" @click="refresh" :disabled="isLoading" class="ml-2">
        <i class="fa" :class="isLoading ? 'fa-spinner fa-spin' : 'fa-refresh'"></i>
        Оновити
      </b-button>
    </div>

    <div v-if="isLoading && !hasLoaded" class="tab-loading text-center text-muted py-4">
      <i class="fa fa-spinner fa-spin mr-1"></i>
      Завантаження...
    </div>

    <div v-else class="tab-content">
      <CashiersTabList
        :items="items"
        :hasLoaded="hasLoaded"
        :getLinkedItems="getLinkedItems"
        :getLinkedItemsTooltip="getLinkedItemsTooltip"
        :getLinkedItemName="getLinkedItemName"
        :formatDateTime="formatDateTime"
        @changeMode="changeMode"
        @openCounterpartiesPopup="openCounterpartiesPopup"
        @toggleActive="toggleActive"
        @toggleAutoShift="toggleAutoShift"
        @toggleShift="toggleShift"
        @openEditModal="openEditModal"
        @confirmDelete="confirmDelete"
      />
    </div>

    <CashierFormModal
      v-if="showModal"
      :is-edit-mode="isEditMode"
      :form="modalForm"
      :errors="modalErrors"
      :is-saving="isSaving"
      :counterparties-loading="counterpartiesLoading"
      :available-counterparties="availableCounterparties"
      :departments-loading="departmentsLoading"
      :departments-list="departmentsList"
      @close="closeModal"
      @save="saveModal"
      @set-mode="setModalMode"
    />

    <CashierDeleteModal
      v-if="showDeleteConfirm"
      :item="deletingItem"
      :is-deleting="isDeleting"
      @close="showDeleteConfirm = false"
      @confirm="deleteItem"
    />

    <CashierCounterpartiesModal
      v-if="showCounterpartiesPopup"
      :title="counterpartyPopupTitle"
      :editing-item="counterpartiesEditingItem"
      :loading="counterpartiesPopupLoading"
      :saving="counterpartiesPopupSaving"
      :options="counterpartiesPopupOptions"
      :mode="counterpartiesPopupMode"
      :selected.sync="counterpartiesPopupSelected"
      @close="closeCounterpartiesPopup"
      @save="saveCounterpartiesPopup"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'CashiersTab',
  components: {
    CashiersTabList: () => import('./CashiersTabList.vue'),
    CashierFormModal: () => import('./CashierFormModal.vue'),
    CashierDeleteModal: () => import('./CashierDeleteModal.vue'),
    CashierCounterpartiesModal: () => import('./CashierCounterpartiesModal.vue'),
  },
  props: {
    active: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isLoading: false,
      hasLoaded: false,
      isSaving: false,
      isDeleting: false,
      items: [],
      departmentsList: [],
      departmentsLoading: false,
      showModal: false,
      isEditMode: false,
      editingItem: null,
      modalForm: {
        license_key: '',
        login: '',
        password: '',
        full_name: '',
        is_active: true,
        auto_shift: true,
        mode: 'sender',
        counterparty_ids: [],
        department_ids: [],
      },
      modalErrors: {},
      showDeleteConfirm: false,
      deletingItem: null,
      // Counterparties for multiselect in edit modal
      counterpartiesLoading: false,
      availableCounterparties: [],
      // Counterparties popup (from list)
      showCounterpartiesPopup: false,
      counterpartiesEditingItem: null,
      counterpartiesPopupLoading: false,
      counterpartiesPopupSaving: false,
      counterpartiesPopupOptions: [],
      counterpartiesPopupSelected: [],
      counterpartiesPopupMode: 'sender',
    };
  },
  computed: {
    ...mapGetters({
      cashiers: 'checkbox/cashiers',
      departments: 'catalog/departments',
    }),
    counterpartyPopupTitle() {
      return this.counterpartiesPopupMode === 'department' ? 'Відділення' : 'Відправники';
    },
  },
  watch: {
    active: {
      immediate: true,
      handler(val) {
        if (val && !this.hasLoaded) {
          this.loadData();
        } else if (val && this.hasLoaded) {
          // Refresh shift statuses when tab becomes active again
          this.loadAllShiftStatuses();
        }
      },
    },
    cashiers: {
      immediate: true,
      handler(data) {
        this.items = [];
        if (data && Array.isArray(data)) {
          data.forEach(item => {
            const npCounterparties = this.normalizeLinkedItems(item.np_counterparties);
            const departments = this.normalizeLinkedItems(item.departments);
            this.items.push({
              ...item,
              mode: item.mode || 'sender',
              np_counterparties: npCounterparties,
              departments,
              _shiftOpened: false,
              _shiftLoading: false,
              _shiftOpenedAt: null,
              _modeLoading: false,
              _counterpartiesLoading: false,
              _activeLoading: false,
              _autoShiftLoading: false,
            });
          });
        }
      },
    },
    departments: {
      immediate: true,
      handler(data) {
        this.departmentsList = data && data.data ? data.data : [];
      },
    },
  },
  methods: {
    normalizeLinkedIds(list) {
      if (!Array.isArray(list)) return [];
      return list
        .map(item => (item && typeof item === 'object' ? item.id : item))
        .filter(id => id !== null && id !== undefined);
    },
    normalizeLinkedItems(list) {
      if (!Array.isArray(list)) return [];
      return list.filter(Boolean);
    },
    async loadData() {
      this.isLoading = true;
      try {
        await Promise.all([
          this.getCashiers(),
          this.getDepartments({ page: 1, per_page: 1000 }),
        ]);
        this.hasLoaded = true;
        // Load shift statuses for all cashiers
        await this.loadAllShiftStatuses();
      } catch (e) {
        this.$toasted.error('Помилка завантаження касирів', { duration: 3000 });
      } finally {
        this.isLoading = false;
      }
    },
    refresh() {
      this.loadData();
    },
    openAddModal() {
      this.isEditMode = false;
      this.editingItem = null;
      this.modalForm = {
        license_key: '',
        login: '',
        password: '',
        full_name: '',
        is_active: true,
        auto_shift: true,
        mode: 'sender',
        counterparty_ids: [],
        department_ids: [],
      };
      this.modalErrors = {};
      this.availableCounterparties = [];
      this.showModal = true;
    },
    async openEditModal(item) {
      this.isEditMode = true;
      this.editingItem = item;
      this.modalForm = {
        license_key: item.license_key || '',
        login: item.login || '',
        password: '',
        full_name: item.full_name || '',
        is_active: item.is_active,
        auto_shift: item.auto_shift,
        mode: item.mode || 'sender',
        counterparty_ids: this.normalizeLinkedIds(item.np_counterparties),
        department_ids: this.normalizeLinkedIds(item.departments),
      };
      this.modalErrors = {};
      this.showModal = true;

      if (this.modalForm.mode === 'sender') {
        await this.loadAvailableCounterparties(item.id);
      } else {
        await this.loadDepartments();
      }
    },
    async loadDepartments() {
      this.departmentsLoading = true;
      try {
        await this.getDepartments({ page: 1, per_page: 1000 });
      } catch (e) {
        this.departmentsList = [];
      } finally {
        this.departmentsLoading = false;
      }
    },
    async setModalMode(mode) {
      if (this.modalForm.mode === mode) return;
      this.modalForm.mode = mode;
      if (!this.isEditMode) return;
      if (mode === 'sender') {
        await this.loadAvailableCounterparties(this.editingItem.id);
      } else {
        await this.loadDepartments();
      }
    },
    async loadAvailableCounterparties(cashierId) {
      this.counterpartiesLoading = true;
      try {
        const res = await this.getAvailableCounterparties(cashierId);
        if (res.status && res.data) {
          // Combine available and already linked counterparties
          const available = this.normalizeLinkedItems(res.data.available || []);
          const linked = this.normalizeLinkedItems(res.data.already_linked || []);
          this.availableCounterparties = [...linked, ...available];
        } else {
          this.availableCounterparties = [];
        }
      } catch (e) {
        this.availableCounterparties = [];
      } finally {
        this.counterpartiesLoading = false;
      }
    },
    closeModal() {
      this.showModal = false;
      this.editingItem = null;
      this.modalErrors = {};
      this.availableCounterparties = [];
    },
    validateModal() {
      this.modalErrors = {};
      if (!this.modalForm.license_key || !this.modalForm.license_key.trim()) {
        this.modalErrors.license_key = 'Ключ ліцензії обов\'язковий';
      }
      if (!this.modalForm.login || !this.modalForm.login.trim()) {
        this.modalErrors.login = 'Логін обов\'язковий';
      }
      if (!this.isEditMode && (!this.modalForm.password || !this.modalForm.password.trim())) {
        this.modalErrors.password = 'Пароль обов\'язковий';
      }
      if (!this.modalForm.full_name || !this.modalForm.full_name.trim()) {
        this.modalErrors.full_name = 'ПІБ обов\'язковий';
      }
      return Object.keys(this.modalErrors).length === 0;
    },
    async saveModal() {
      if (!this.validateModal()) {
        return;
      }
      this.isSaving = true;
      try {
        const payload = {
          license_key: this.modalForm.license_key,
          login: this.modalForm.login,
          password: this.modalForm.password,
          full_name: this.modalForm.full_name,
          is_active: this.modalForm.is_active,
          auto_shift: this.modalForm.auto_shift,
          mode: this.modalForm.mode,
        };

        if (this.isEditMode) {
          if (!payload.password) {
            delete payload.password;
          }
          const res = await this.updateCashier({ id: this.editingItem.id, data: payload });
          if (res.status) {
            // Sync counterparties if mode is sender
            if (this.modalForm.mode === 'sender') {
              const syncRes = await this.syncCounterparties({
                cashierId: this.editingItem.id,
                counterpartyIds: this.modalForm.counterparty_ids || [],
              });
              if (!syncRes.status && syncRes.data) {
                // Show conflict errors
                if (syncRes.data.conflicts && syncRes.data.conflicts.length) {
                  this.$toasted.error(syncRes.data.conflicts.join('\n'), { duration: 7000 });
                } else {
                  this.$toasted.error(syncRes.data.message || 'Помилка синхронізації контрагентів', { duration: 5000 });
                }
              }
            } else {
              await this.syncDepartments({
                cashierId: this.editingItem.id,
                departmentIds: this.modalForm.department_ids || [],
                currentDepartmentIds: this.editingItem.departments
                  ? this.editingItem.departments.map(d => d.id)
                  : [],
              });
            }
            this.$toasted.success('Касира оновлено', { duration: 3000 });
            this.closeModal();
            this.loadData();
          } else {
            this.$toasted.error((res.data && res.data.message) || 'Помилка оновлення', { duration: 3000 });
          }
        } else {
          const res = await this.createCashier(payload);
          if (res.status) {
            this.$toasted.success('Касира створено', { duration: 3000 });
            this.closeModal();
            this.loadData();
          } else {
            this.$toasted.error((res.data && res.data.message) || 'Помилка створення', { duration: 3000 });
          }
        }
      } catch (e) {
        this.$toasted.error('Помилка збереження', { duration: 3000 });
      } finally {
        this.isSaving = false;
      }
    },
    buildCashierUpdatePayload(item, overrides = {}) {
      return {
        license_key: item.license_key,
        login: item.login,
        full_name: item.full_name,
        is_active: item.is_active,
        auto_shift: item.auto_shift,
        mode: item.mode || 'sender',
        ...overrides,
      };
    },
    normalizeToggleValue(value) {
      if (value && typeof value === 'object' && Object.prototype.hasOwnProperty.call(value, 'value')) {
        return value.value;
      }
      return value;
    },
    async toggleActive(item, value) {
      const idx = this.items.findIndex(i => i.id === item.id);
      if (idx === -1) return;

      const nextValue = this.normalizeToggleValue(value);
      const prevValue = item.is_active;
      const prevShiftOpened = item._shiftOpened;
      this.$set(this.items[idx], 'is_active', nextValue);
      this.$set(this.items[idx], '_activeLoading', true);

      try {
        const payload = this.buildCashierUpdatePayload(this.items[idx], { is_active: nextValue });
        const res = await this.updateCashier({ id: item.id, data: payload });
        if (res.status) {
          if (!nextValue) {
            this.$set(this.items[idx], '_shiftOpened', false);
            this.$set(this.items[idx], '_shiftOpenedAt', null);
          }
          this.$toasted.success('Статус касира оновлено', { duration: 3000 });
        } else {
          this.$set(this.items[idx], 'is_active', prevValue);
          this.$set(this.items[idx], '_shiftOpened', prevShiftOpened);
          this.$toasted.error((res.data && res.data.message) || 'Помилка оновлення', { duration: 3000 });
        }
      } catch (e) {
        this.$set(this.items[idx], 'is_active', prevValue);
        this.$set(this.items[idx], '_shiftOpened', prevShiftOpened);
        this.$toasted.error('Помилка оновлення', { duration: 3000 });
      } finally {
        this.$set(this.items[idx], '_activeLoading', false);
      }
    },
    async toggleAutoShift(item, value) {
      const idx = this.items.findIndex(i => i.id === item.id);
      if (idx === -1) return;

      const nextValue = this.normalizeToggleValue(value);
      const prevValue = item.auto_shift;
      this.$set(this.items[idx], 'auto_shift', nextValue);
      this.$set(this.items[idx], '_autoShiftLoading', true);

      try {
        const payload = this.buildCashierUpdatePayload(this.items[idx], { auto_shift: nextValue });
        const res = await this.updateCashier({ id: item.id, data: payload });
        if (res.status) {
          this.$toasted.success('Авто зміну оновлено', { duration: 3000 });
        } else {
          this.$set(this.items[idx], 'auto_shift', prevValue);
          this.$toasted.error((res.data && res.data.message) || 'Помилка оновлення', { duration: 3000 });
        }
      } catch (e) {
        this.$set(this.items[idx], 'auto_shift', prevValue);
        this.$toasted.error('Помилка оновлення', { duration: 3000 });
      } finally {
        this.$set(this.items[idx], '_autoShiftLoading', false);
      }
    },
    confirmDelete(item) {
      this.deletingItem = item;
      this.showDeleteConfirm = true;
    },
    async deleteItem() {
      if (!this.deletingItem) return;
      this.isDeleting = true;
      try {
        const res = await this.deleteCashier(this.deletingItem.id);
        if (res.status) {
          this.$toasted.success('Касира видалено', { duration: 3000 });
          this.showDeleteConfirm = false;
          this.deletingItem = null;
          this.loadData();
        } else {
          this.$toasted.error((res.data && res.data.message) || 'Помилка видалення', { duration: 3000 });
        }
      } catch (e) {
        this.$toasted.error('Помилка видалення', { duration: 3000 });
      } finally {
        this.isDeleting = false;
      }
    },
    // Shift Management Methods

    // Counterparties popup methods
    async openCounterpartiesPopup(item) {
      const idx = this.items.findIndex(i => i.id === item.id);
      if (idx !== -1) {
        this.$set(this.items[idx], '_counterpartiesLoading', true);
      }

      this.counterpartiesEditingItem = item;
      this.counterpartiesPopupMode = item.mode === 'department' ? 'department' : 'sender';
      this.counterpartiesPopupSelected = this.counterpartiesPopupMode === 'department'
        ? this.normalizeLinkedIds(item.departments)
        : this.normalizeLinkedIds(item.np_counterparties);
      this.counterpartiesPopupOptions = [];
      this.counterpartiesPopupLoading = true;
      this.showCounterpartiesPopup = true;

      try {
        if (this.counterpartiesPopupMode === 'department') {
          await this.loadDepartments();
          this.counterpartiesPopupOptions = this.normalizeLinkedItems(this.departmentsList || []);
        } else {
          const res = await this.getAvailableCounterparties(item.id);
          if (res.status && res.data) {
            const available = this.normalizeLinkedItems(res.data.available || []);
            const linked = this.normalizeLinkedItems(res.data.already_linked || []);
            this.counterpartiesPopupOptions = [...linked, ...available];
          } else {
            this.counterpartiesPopupOptions = [];
          }
        }
      } catch (e) {
        this.counterpartiesPopupOptions = [];
        this.$toasted.error(this.counterpartiesPopupMode === 'department'
          ? 'Помилка завантаження відділень'
          : 'Помилка завантаження контрагентів', { duration: 3000 });
      } finally {
        this.counterpartiesPopupLoading = false;
        if (idx !== -1) {
          this.$set(this.items[idx], '_counterpartiesLoading', false);
        }
      }
    },
    closeCounterpartiesPopup() {
      this.showCounterpartiesPopup = false;
      this.counterpartiesEditingItem = null;
      this.counterpartiesPopupOptions = [];
      this.counterpartiesPopupSelected = [];
      this.counterpartiesPopupMode = 'sender';
    },
    async saveCounterpartiesPopup() {
      if (!this.counterpartiesEditingItem) return;

      this.counterpartiesPopupSaving = true;
      try {
        if (this.counterpartiesPopupMode === 'department') {
          await this.syncDepartments({
            cashierId: this.counterpartiesEditingItem.id,
            departmentIds: this.counterpartiesPopupSelected || [],
          });
          this.$toasted.success('Відділення збережено', { duration: 3000 });
          this.closeCounterpartiesPopup();
          this.loadData();
        } else {
          const syncRes = await this.syncCounterparties({
            cashierId: this.counterpartiesEditingItem.id,
            counterpartyIds: this.counterpartiesPopupSelected || [],
          });

          if (syncRes.status) {
            this.$toasted.success('Контрагенти збережено', { duration: 3000 });
            this.closeCounterpartiesPopup();
            this.loadData();
          } else {
            if (syncRes.data && syncRes.data.conflicts && syncRes.data.conflicts.length) {
              this.$toasted.error(syncRes.data.conflicts.join('\n'), { duration: 7000 });
            } else {
              this.$toasted.error((syncRes.data && syncRes.data.message) || 'Помилка синхронізації контрагентів', { duration: 5000 });
            }
          }
        }
      } catch (e) {
        this.$toasted.error(this.counterpartiesPopupMode === 'department'
          ? 'Помилка збереження відділень'
          : 'Помилка збереження контрагентів', { duration: 3000 });
      } finally {
        this.counterpartiesPopupSaving = false;
      }
    },
    getLinkedItems(item) {
      const list = item.mode === 'department'
        ? item.departments
        : item.np_counterparties;
      return this.normalizeLinkedItems(list);
    },
    getLinkedItemName(item) {
      if (!item) return '—';
      if (typeof item !== 'object') return String(item);
      return item.name || item.title || item.full_name || '—';
    },
    getLinkedItemsTooltip(item) {
      const items = this.getLinkedItems(item);
      if (!items.length) {
        return item.mode === 'department'
          ? 'Натисніть щоб додати відділення'
          : 'Натисніть щоб додати контрагентів';
      }
      const names = items.map(linked => this.getLinkedItemName(linked)).join(', ');
      return names.length > 100 ? names.substring(0, 100) + '...' : names;
    },
    async syncDepartments({ cashierId, departmentIds, currentDepartmentIds = null }) {
      const current = currentDepartmentIds
        ? currentDepartmentIds
        : (this.counterpartiesEditingItem?.departments
          ? this.counterpartiesEditingItem.departments.map(d => d.id)
          : []);
      const next = new Set(departmentIds || []);
      const toUnlink = current.filter(id => !next.has(id));
      const toLink = (departmentIds || []).filter(id => !current.includes(id));

      const updates = [];
      toUnlink.forEach(id => {
        const dept = this.departmentsList.find(d => d.id === id);
        if (!dept) return;
        updates.push(this.editDepartment({
          id: dept.id,
          data: {
            title: dept.title,
            sort: dept.sort,
            checkbox_cashier_id: null,
          },
        }));
      });
      toLink.forEach(id => {
        const dept = this.departmentsList.find(d => d.id === id);
        if (!dept) return;
        updates.push(this.editDepartment({
          id: dept.id,
          data: {
            title: dept.title,
            sort: dept.sort,
            checkbox_cashier_id: cashierId,
          },
        }));
      });

      if (updates.length) {
        await Promise.all(updates);
      }
    },
    // Mode change from table
    async changeMode(item, newMode) {
      if (item.mode === newMode) return;

      const idx = this.items.findIndex(i => i.id === item.id);
      if (idx === -1) return;

      this.$set(this.items[idx], '_modeLoading', true);

      try {
        const res = await this.updateCashier({
          id: item.id,
          data: { mode: newMode }
        });

        if (res.status) {
          this.$set(this.items[idx], 'mode', newMode);
          this.$toasted.success(`Режим змінено на "${newMode === 'sender' ? 'Відправник' : 'Відділ'}"`, { duration: 2000 });
        } else {
          this.$toasted.error((res.data && res.data.message) || 'Помилка зміни режиму', { duration: 3000 });
        }
      } catch (e) {
        this.$toasted.error('Помилка зміни режиму', { duration: 3000 });
      } finally {
        this.$set(this.items[idx], '_modeLoading', false);
      }
    },
    async loadAllShiftStatuses() {
      const activeItems = this.items.filter(item => item.is_active);
      if (!activeItems.length) return;

      // Set loading state for all active cashiers
      activeItems.forEach(item => {
        const idx = this.items.findIndex(i => i.id === item.id);
        if (idx !== -1) {
          this.$set(this.items[idx], '_shiftLoading', true);
        }
      });

      // Load statuses in parallel
      await Promise.all(
        activeItems.map(item => this.refreshShiftStatus(item))
      );
    },
    async refreshShiftStatus(item) {
      const idx = this.items.findIndex(i => i.id === item.id);
      if (idx === -1) return;

      try {
        const res = await this.getShiftStatus(item.id);
        if (res.status && res.data && res.data.models) {
          this.$set(this.items[idx], '_shiftOpened', res.data.models.is_opened || false);
          this.$set(this.items[idx], '_shiftOpenedAt', (res.data.models.local_shift && res.data.models.local_shift.opened_at) || null);
        } else {
          this.$set(this.items[idx], '_shiftOpened', false);
          this.$set(this.items[idx], '_shiftOpenedAt', null);
        }
      } catch (e) {
        this.$set(this.items[idx], '_shiftOpened', false);
        this.$set(this.items[idx], '_shiftOpenedAt', null);
      } finally {
        this.$set(this.items[idx], '_shiftLoading', false);
      }
    },
    async toggleShift(item, event) {
      const idx = this.items.findIndex(i => i.id === item.id);
      if (idx === -1) return;

      const shouldOpen = event.value;
      this.$set(this.items[idx], '_shiftLoading', true);

      try {
        const res = shouldOpen
          ? await this.openShift(item.id)
          : await this.closeShift(item.id);

        if (res.status && res.data && res.data.models) {
          this.$set(this.items[idx], '_shiftOpened', res.data.models.is_opened || false);
          this.$set(this.items[idx], '_shiftOpenedAt', (res.data.models.local_shift && res.data.models.local_shift.opened_at) || null);
          this.$toasted.success(
            shouldOpen ? 'Зміну відкрито' : 'Зміну закрито',
            { duration: 3000 }
          );
        } else {
          this.$toasted.error((res.data && res.data.message) || 'Помилка оновлення зміни', { duration: 3000 });
          // Refresh to get correct state
          await this.refreshShiftStatus(item);
        }
      } catch (e) {
        this.$toasted.error('Помилка оновлення зміни', { duration: 3000 });
        // Refresh to get correct state
        await this.refreshShiftStatus(item);
      } finally {
        this.$set(this.items[idx], '_shiftLoading', false);
      }
    },
    formatDateTime(dateString) {
      if (!dateString) return '';
      try {
        const date = new Date(dateString);
        return date.toLocaleString('uk-UA', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
      } catch (e) {
        return dateString;
      }
    },
    ...mapActions({
      getCashiers: 'checkbox/getCashiers',
      createCashier: 'checkbox/createCashier',
      updateCashier: 'checkbox/updateCashier',
      deleteCashier: 'checkbox/deleteCashier',
      getShiftStatus: 'checkbox/getShiftStatus',
      openShift: 'checkbox/openShift',
      closeShift: 'checkbox/closeShift',
      getAvailableCounterparties: 'checkbox/getAvailableCounterparties',
      syncCounterparties: 'checkbox/syncCounterparties',
      getDepartments: 'catalog/getDepartments',
      editDepartment: 'catalog/editDepartment',
    }),
  },
};
</script>

<style lang="scss" scoped>
.checkbox-cashiers-tab {
  padding: 15px;
  min-height: 300px;
}

.tab-toolbar {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
}

.tab-content {
  .table {
    margin-bottom: 0;
    font-size: 13px;

    th {
      background: #f8f9fa;
      font-weight: 600;
      white-space: nowrap;
    }

    td {
      vertical-align: middle;
    }
  }

  .actions-cell {
    white-space: nowrap;
    text-align: center;

    .btn-link {
      padding: 2px 6px;
    }
  }

  .shift-cell {
    .shift-toggle-wrapper {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 40px;
      min-height: 20px;
      cursor: pointer;
    }
  }

  .mode-cell {
    .btn-group {
      .mode-btn {
        padding: 2px 6px;
        font-size: 11px;
      }
    }
  }

  .counterparties-cell {
    .counterparties-btn {
      padding: 2px 8px;
      color: #6c757d;
      text-decoration: none;

      .edit-icon {
        opacity: 0;
        font-size: 10px;
        transition: opacity 0.2s;
      }

      &:hover {
        color: #495057;

        .edit-icon {
          opacity: 1;
        }
      }

      &.has-items {
        color: #17a2b8;
      }
    }

    .counterparties-names {
      margin-top: 2px;
      font-size: 9px;
      line-height: 1.2;
      color: #6c757d;
      max-width: 200px;
      white-space: normal;
      word-break: break-word;
    }

    .counterparty-name {
      line-height: 1.05;
    }
  }
}

 
</style>
